import { Password, Visibility, VisibilityOff } from "@mui/icons-material";
import {
  FormControl,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import React, { useState } from "react";

export const CustomInput = ({
  mT,
  mB,
  placeholder,
  value,
  setValue,
  Icon,
  noIcon,
  rightIcon,
  disabled,
  type,
  width,
  label,
}) => {
  return (
    <FormControl
      fullWidth
      sx={{
        mt: mT ? 2 : 0,
        mb: mB ? mB : 2,
        alignItems: "center",
      }}
    >
      {label && (
        <Typography
          sx={{
            mb: 1,
            textAlign: "left",
            width: "100%",
          }}
        >
          {label}
        </Typography>
      )}
      <TextField
        sx={{
          width: width || "100%",
          backgroundColor: "transparent",
          fontFamily: "Inter-Regular",
          fontSize: "14px",
          fontWeight: "400",
          borderRadius: "0px",
          "& .MuiOutlinedInput-root": {
            borderRadius: "0px",
          },
          border: "1px solid #000",
        }}
        type={type ? type : "text"}
        placeholder={placeholder}
        value={value}
        disabled={disabled}
        onChange={(e) => setValue(e.target.value)}
        InputProps={{
          sx: {
            height: "50px",
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: "#E0E2E7",
            },
            "&:hover .MuiOutlinedInput-notchedOutline": {
              borderColor: "#A0A2A7",
            },
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
              borderColor: "#707173",
            },
            "& .MuiInputAdornment-root": {
              color: "#00000080",
            },
          },
          startAdornment: (
            <>
              {!noIcon && !rightIcon && (
                <InputAdornment
                  position="start"
                  sx={{
                    paddingRight: "5px",
                  }}
                >
                  <Icon sx={{ color: "#00000080", fontSize: "20px" }} />
                </InputAdornment>
              )}
            </>
          ),
          endAdornment: (
            <>
              {rightIcon && (
                <InputAdornment
                  position="start"
                  sx={{
                    paddingRight: "5px",
                  }}
                >
                  <Icon sx={{ color: "#00000080", fontSize: "20px" }} />
                </InputAdornment>
              )}
            </>
          ),
        }}
        InputLabelProps={{
          shrink: false, // Prevents the label from floating up when typing
        }}
      />
    </FormControl>
  );
};

export const CustomPasswordInput = ({
  mT,
  mB,
  placeholder,
  value,
  setValue,
  Icon,
  label,
}) => {
  const [showPassword, setShowPassword] = useState(false);

  const handleClickShowPassword = () => setShowPassword((show) => !show);

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };
  return (
    <FormControl fullWidth sx={{ mt: mT ? 2 : 0, mb: mB ? 2 : 0 }}>
      {label && (
        <Typography
          sx={{
            mb: 1,
            textAlign: "left",
            width: "100%",
          }}
        >
          {label}
        </Typography>
      )}
      <TextField
        type={showPassword ? "text" : "password"}
        sx={{
          backgroundColor: "transparent",
          fontFamily: "Inter-Regular",
          fontSize: "14px",
          fontWeight: "400",
          borderRadius: "0px",
          "& .MuiOutlinedInput-root": {
            borderRadius: "0px",
          },
          border: "1px solid #000",
        }}
        placeholder={placeholder}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        InputProps={{
          sx: {
            height: "50px",
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: "#E0E2E7",
            },
            "&:hover .MuiOutlinedInput-notchedOutline": {
              borderColor: "#A0A2A7",
            },
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
              borderColor: "#707173",
            },
            "& .MuiInputAdornment-root": {
              color: "#00000080",
            },
          },
          startAdornment: (
            <InputAdornment
              position="start"
              sx={{
                paddingRight: "5px",
              }}
            >
              <Password sx={{ color: "#00000080", fontSize: "20px" }} />
            </InputAdornment>
          ),
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                aria-label="toggle password visibility"
                onClick={handleClickShowPassword}
                onMouseDown={handleMouseDownPassword}
                edge="end"
              >
                {showPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          ),
        }}
        InputLabelProps={{
          shrink: false,
        }}
      />
    </FormControl>
  );
};

export const CustomTextArea = ({
  mT,
  mB,
  placeholder,
  value,
  setValue,
  Icon,
  noIcon,
  limit,
}) => {
  const handleInputChange = (e) => {
    const text = e.target.value;
    const words = text.trim().split(/\s+/);

    if (!limit || words.length <= limit || text === "") {
      setValue(text); // ✅ Only update if within limit or no limit
    }
  };
  return (
    <FormControl fullWidth sx={{ mt: mT ? 2 : 0, mb: mB ? mB : 2 }}>
      <TextField
        sx={{
          backgroundColor: "transparent",
          fontFamily: "Inter-Regular",
          fontSize: "14px",
          fontWeight: "400",
          borderRadius: "0px",
          "& .MuiOutlinedInput-root": {
            borderRadius: "0px",
          },
          border: "1px solid #000",
        }}
        placeholder={placeholder}
        value={value}
        onChange={handleInputChange}
        multiline
        rows={4}
        InputProps={{
          sx: {
            height: "auto",
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: "#E0E2E7",
            },
            "&:hover .MuiOutlinedInput-notchedOutline": {
              borderColor: "#A0A2A7",
            },
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
              borderColor: "#707173",
            },
            "& .MuiInputAdornment-root": {
              color: "#00000080",
            },
          },
          startAdornment: (
            <>
              {!noIcon && (
                <InputAdornment
                  position="start"
                  sx={{
                    paddingRight: "5px",
                  }}
                >
                  <Icon sx={{ color: "#00000080", fontSize: "20px" }} />
                </InputAdornment>
              )}
            </>
          ),
        }}
        InputLabelProps={{
          shrink: false,
        }}
      />
    </FormControl>
  );
};
